import React, { useEffect, useState } from "react";
import PaginatedTable from "../common/PaginatedTable";
import DTIPages from "../common/DTIPages";
import { View } from "@instructure/ui-view";
import { Text } from "@instructure/ui-text";
import DtiToolbar from "../common/DtiToolbar";
import DtiKpiGrid from "../common/DtiKpiGrid";
import DtiSectionHeading from "../common/DtiSectionHeading";
import DtiOverviewSkeleton from "../common/DtiSkeleton";
import { downloadWithAuth } from "../../../utils";
import {
  getJobContentsRate,
  getContentsRate,
  createJobContentsRate,
} from "../api/api";

export default function DigitalLearning({ iframe = false }) {
  const [data, setData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    let intervalId;
    const fetchData = async () => {
      setIsFetching(true);
      try {
        const dataContentsRate = await getContentsRate();
        setData(dataContentsRate);

        const job = await getJobContentsRate();
        if (["starting", "pending"].includes(job?.workflowState)) {
          setIsLoading(true);
          intervalId = setInterval(async () => {
            const jobStatus = await getJobContentsRate();
            if (jobStatus?.workflowState === "done") {
              setIsLoading(false);
              clearInterval(intervalId);
              const fresh = await getContentsRate();
              setData(fresh);
            }
          }, 1000);
        }
      } finally {
        setIsFetching(false);
      }
    };
    fetchData();
    return () => clearInterval(intervalId);
  }, []);

  const handleJob = async () => {
    if (isLoading) return;
    setIsLoading(true);
    await createJobContentsRate();
    const intervalId = setInterval(async () => {
      const job = await getJobContentsRate();
      if (job?.workflowState === "done") {
        setIsLoading(false);
        clearInterval(intervalId);
        setIsFetching(true);
        try {
          const fresh = await getContentsRate();
          setData(fresh);
        } finally {
          setIsFetching(false);
        }
      }
    }, 1000);
  };

  const showSkeleton = isFetching || isLoading;

  const courseRows = Array.isArray(data?.dataRate) ? data.dataRate : [];
  const totalCourses = data?.totalCourses ?? courseRows.length;
  const totalCoursesWithContents = data?.totalCoursesWithContents
    ?? courseRows.filter((row) => (row?.total_contents || 0) > 0).length;
  const contentRate = totalCourses > 0
    ? `${((totalCoursesWithContents / totalCourses) * 100).toFixed(2)}%`
    : null;

  const ctgdptKpiItems = (Array.isArray(data?.rateKnowledgeUnits)
    ? data.rateKnowledgeUnits
    : []
  ).map((row) => ({
    id: `ctgdpt-${row.ten_cap_hoc}`,
    label: `Triển khai nội dung chương trình giáo dục phổ thông dưới hình thức trực tuyến đạt 25% ở bậc tiểu học, 35% ở bậc trung học (${row.ten_cap_hoc})`,
    value: row.rate != null && row.rate !== "" ? `${row.rate}%` : null,
  }));

  const handleExport = async (event) => {
    event.preventDefault();
    if (isExporting) return;
    setIsExporting(true);
    try {
      await downloadWithAuth(
        "/api/v1/contents/export",
        "6.3-hoc-lieu-so.xlsx",
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        "[LỖI] Xuất báo cáo học liệu số thất bại:",
        error && error.message ? error.message : error,
      );
    } finally {
      setIsExporting(false);
    }
  };

  if (iframe) {
    return (
      <View as="div" margin="small" textAlign="center">
        <Text size="large">Bạn không được quyền truy cập</Text>
      </View>
    );
  }

  if (showSkeleton) {
    return <DtiOverviewSkeleton kpiCount={6} showExport />;
  }

  return (
    <>
      <DtiToolbar
        isLoading={isLoading}
        onRefresh={handleJob}
        onExport={handleExport}
        isExporting={isExporting}
        exportLabel="Tải báo cáo Excel"
      />

      <DTIPages
        secondPage={
          <>
            <DtiSectionHeading>Chi tiết theo khóa học</DtiSectionHeading>
            <div className="dti-detail-block">
              <ul>
                <li>
                  Tổng số lớp: <strong>{totalCourses || "—"}</strong>
                </li>
                <li>
                  Tổng số lớp có học liệu:{" "}
                  <strong>{totalCoursesWithContents || "—"}</strong>
                </li>
                <li>
                  Tỉ lệ lớp có học liệu:{" "}
                  <strong>{contentRate ?? (data?.totalRate != null ? `${data.totalRate}%` : "—")}</strong>
                </li>
              </ul>
            </div>
            <PaginatedTable
              headers={[
                { id: "course_name", text: "Tê khoá học", width: "75%" },
                { id: "total_contents", text: "Số lượng nội dung", width: "50%" },
              ]}
              rows={data?.dataRate}
              perPage={10}
            />

            <DtiSectionHeading>
            Triển khai nội dung chương trình giáo dục phổ thông dưới hình thức trực tuyến đạt 25% ở bậc tiểu học, 35% ở bậc trung học
            </DtiSectionHeading>
            <PaginatedTable
              headers={[
                { id: "ten_cap_hoc", text: "Cấp học", width: "75%" },
                { id: "rate", text: "Tỷ lệ", width: "25%" },
              ]}
              rows={data?.rateKnowledgeUnits || []}
              perPage={10}
            />
          </>
        }
        firstPage={
          <DtiKpiGrid
            items={[
              {
                id: "totalCoursesWithContents",
                label: "Tổng số lớp có học liệu",
                value: totalCoursesWithContents || null,
              },
              {
                id: "totalCourses",
                label: "Tổng số lớp trên LMS",
                value: totalCourses || null,
              },
              {
                id: "totalRate",
                label: "Tỉ lệ lớp có học liệu",
                value: contentRate ?? (data?.totalRate != null ? `${data.totalRate}%` : null),
              },
              ...ctgdptKpiItems,
            ]}
          />
        }
      />
    </>
  );
}
